import { useCallback } from 'react';
import type { Identity } from '../types';
import { getAmplitudeAITenantId } from '../utils/getAmplitudeAITenantId';
import { useIdentity } from './useIdentity';

export enum AmplitudeSearchEvents {
  CLICK_SEARCH_HISTORY = 'click search history',
  CLOSE_SEARCH_BAR = 'close search bar',
  OPEN_SEARCH_BAR = 'open search bar',
  QUICK_SEARCH = 'quick search',
  VIEW_QUICK_SEARCH_RESULT = 'view quick search result',
}

export enum AmplitudeCopilotEvents {
  CLICK_COPILOT = 'copilot clicked',
  CLICK_COPILOT_FEEDBACK = 'click copilot feedback',
  CLICK_COPILOT_SUGGESTION = 'click copilot suggestion',
  SEND_COPILOT_MESSAGE = 'send copilot message',
}

export enum AmplitudeSupportDeflectionEvents {
  CLICK_AUTHORIZE_BUTTON = 'click authorize button',
  CLICK_EXISTING_TICKETS = 'click existing tickets',
  CLICK_SUPPORT_ARTICLES = 'click support articles',
  CLICK_VIEW_ALL_ARTICLES = 'click view all articles',
  ENTER_SEARCH_ITEM = 'enter search item',
  OPEN_SUPPORT_DEFLECTION = 'open support deflection',
  OPEN_ZUORA_AI = 'opened zuora ai',
  PROVIDED_FEEDBACK = 'provided feedback',
  TICKET_CREATED = 'ticket created',
}

export enum AmplitudeNavEvents {
  CLICK_CUSTOM_OBJECT_MENU_ITEM = 'click custom object menu item',
  CLICK_EOL_LINK = 'click eol link',
  CLICK_HOME_MENU_ITEM = 'click home menu item',
  CLICK_ISH_LINK = 'click ish link',
  CLICK_NEW_UI_EXPERIENCE_BAR = 'click new ui experience bar',
  ENABLE_NEW_UI_EXPERIENCE = 'enable new ui experience',
  GLOBAL_NAV_LOADED = 'global nav loaded',
  INTERACT_WITH_CHATBOT = 'interact with chatbot',
  OPT_OUT_NEW_UI_EXPERIENCE = 'opt out new ui experience',
  REPORT_WEB_VITALS = 'report web vitals',
}

export enum AmplitudeConciergeEvents {
  // Entry point—measures intent
  SIDE_PANEL_OPENED = 'side_panel_opened',

  // Primary engagement signal
  SIDE_MESSAGE_SENT = 'side_message_sent',

  // Action execution signal
  SIDE_ACTION_EXECUTED = 'side_action_executed',

  // Welcome banner prompt template engagement
  SIDE_PROMPT_TEMPLATE_CLICKED = 'side_prompt_template_clicked',

  // Confirms AI round-trip success
  SIDE_RESPONSE_RECEIVED = 'side_response_received',
}

export enum AmplitudeMiscEvents {
  CLICK_OPT_OUT_UI_EXPERIENCE_BAR = 'click opt out ui experience bar',
  PAGE_VIEWED = 'page viewed',
  PRESS_BUTTON = 'press button',

  // Top of activation funnel
  AI_TERMS_MODAL_VIEWED = 'ai_terms_modal_viewed',

  // Activation gate—conversion to AI-enabled users
  AI_TERMS_ACCEPTED = 'ai_terms_accepted',

  // Quality signal on AI responses
  AI_FEEDBACK_POSITIVE = 'ai_feedback_positive',
  AI_FEEDBACK_POSITIVE_REMOVED = 'ai_feedback_positive_removed',
  AI_FEEDBACK_NEGATIVE = 'ai_feedback_negative',
  AI_FEEDBACK_NEGATIVE_REMOVED = 'ai_feedback_negative_removed',

  // Share Feedback modal (AI-391): detail submitted or canceled
  AI_FEEDBACK_DETAIL_SUBMITTED = 'ai_feedback_detail_submitted',
  AI_FEEDBACK_DETAIL_CANCELED = 'ai_feedback_detail_canceled',
}

export enum AmplitudeNarrativeSummaryEvents {
  NARRATIVE_SUMMARY_DISPLAYED = 'narrative_summary_displayed',
  NARRATIVE_SUMMARY_DISMISSED = 'narrative_summary_dismissed',
  NARRATIVE_SUMMARY_LINK_CLICKED = 'narrative_summary_link_clicked',
  NARRATIVE_SUMMARIZE_CLICKED = 'narrative_summarize_clicked',
  NARRATIVE_SUMMARY_GENERATED = 'narrative_summary_generated',
  NARRATIVE_SUMMARY_ERROR = 'narrative_summary_error',
}

/** Event types that must carry AI-871 `tenant_id` (Revenue vs Billing attribution). */
const ZUORA_AI_AMPLITUDE_EVENT_TYPES = new Set<string>([
  ...Object.values(AmplitudeConciergeEvents),
  ...Object.values(AmplitudeCopilotEvents),
  ...Object.values(AmplitudeNarrativeSummaryEvents),
  AmplitudeMiscEvents.AI_TERMS_MODAL_VIEWED,
  AmplitudeMiscEvents.AI_TERMS_ACCEPTED,
  AmplitudeMiscEvents.AI_FEEDBACK_POSITIVE,
  AmplitudeMiscEvents.AI_FEEDBACK_POSITIVE_REMOVED,
  AmplitudeMiscEvents.AI_FEEDBACK_NEGATIVE,
  AmplitudeMiscEvents.AI_FEEDBACK_NEGATIVE_REMOVED,
  AmplitudeMiscEvents.AI_FEEDBACK_DETAIL_SUBMITTED,
  AmplitudeMiscEvents.AI_FEEDBACK_DETAIL_CANCELED,
  AmplitudeSupportDeflectionEvents.OPEN_ZUORA_AI,
]);

const isZuoraAIAmplitudeEvent = (eventType: string) =>
  ZUORA_AI_AMPLITUDE_EVENT_TYPES.has(eventType);

let isInited = false;
/** Shared promise so concurrent logEvent calls await the same init and amplitude.init() runs only once. */
let initPromise: Promise<void> | null = null;

export const useAmplitude = () => {
  const { data: identity } = useIdentity();

  const getDefaultEventProps = () => {
    return {
      url: window.location.href,
    };
  };

  const logEvent = useCallback(
    /**
     * From https://www.docs.developers.amplitude.com/data/sdks/javascript/#event-properties:
     * - Valid data types for event properties are string, array, object, boolean, and number.
     * - Object keys have a 1000 character limit.
     */
    (
      eventType: string,
      eventProperties?: { [key: string]: string | number },
    ) => {
      import('./useAmplitudeInternal').then(
        async ({ track, init, syncAmplitudeTenantIdentify }) => {
          if (identity?.coreUserId && !isInited) {
            if (!initPromise) {
              initPromise = (async () => {
                try {
                  await init(identity as Identity);
                  isInited = true;
                } catch (e) {
                  initPromise = null; // allow retry on next logEvent
                  throw e;
                }
              })();
            }
            await initPromise;
          }

          if (isInited && identity?.tenantId) {
            syncAmplitudeTenantIdentify(identity as Identity);
          }

          track(eventType, {
            ...getDefaultEventProps(),
            ...eventProperties,
            ...(isZuoraAIAmplitudeEvent(eventType)
              ? { tenant_id: getAmplitudeAITenantId(identity?.tenantId) }
              : {}),
          });
        },
      );
    },
    [identity],
  );

  const pressButton = (eventDetails: {
    actionLabel?: string;
    objectName?: string;
    columnHeaderLabel?: string;
    filterCriteria?: string;
  }) => {
    logEvent(AmplitudeMiscEvents.PRESS_BUTTON, eventDetails);
  };

  return {
    logEvent,
    pressButton,
  };
};
